
import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";

// Define the church branding data structure
export interface ChurchBranding {
  churchName: string;
  logoUrl: string | null;
  primaryColor: string;
  secondaryColor: string;
  welcomeMessage: string | null;
  churchCode: string;
}

interface ChurchBrandingContextType {
  branding: ChurchBranding | null;
  loading: boolean;
  error: Error | null;
  refreshBranding: () => Promise<void>;
}

// Create the context with a default value
const ChurchBrandingContext = createContext<ChurchBrandingContextType>({
  branding: null,
  loading: true,
  error: null,
  refreshBranding: async () => {}
});

// Custom hook to use the context
export const useChurchBranding = () => useContext(ChurchBrandingContext);

interface ChurchBrandingProviderProps {
  children: ReactNode;
}

export const ChurchBrandingProvider = ({ children }: ChurchBrandingProviderProps) => {
  const [branding, setBranding] = useState<ChurchBranding | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Function to fetch church branding data
  const fetchChurchBranding = useCallback(async () => {
    setLoading(true);
    try {
      console.log("Fetching church branding data...");
      
      // For URL-based church association (like bracelets with church codes)
      // Standardize on 'id' parameter but still accept 'code' for backward compatibility
      const urlParams = new URLSearchParams(window.location.search);
      const braceletCode = urlParams.get('id') || urlParams.get('code');
      
      let brandingData: ChurchBranding;
      
      if (braceletCode) {
        const cleanBraceletCode = braceletCode.replace(/^GHG-?/, '').replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
        console.log("Found bracelet code in URL:", cleanBraceletCode);
        
        // Special case for TEST series bracelets
        if (cleanBraceletCode.startsWith('TEST')) {
          console.log("TEST series bracelet detected, fetching TEST COMM branding");
          
          const { data } = await supabase
            .from('church_branding')
            .select('*')
            .eq('church_code', '001')
            .maybeSingle();
            
          if (data) {
            console.log("Found TEST COMM branding for TEST series bracelet");
            brandingData = {
              churchName: data.church_name,
              logoUrl: data.logo_url,
              primaryColor: data.primary_color || '#0F4C81',
              secondaryColor: data.secondary_color || '#2A6BAA',
              welcomeMessage: data.custom_welcome_message,
              churchCode: data.church_code
            };
            
            setBranding(brandingData);
            setLoading(false);
            return;
          }
        }
      }
      
      // Check for user-specific church code (for church admins or users with a specific congregation)
      const session = await supabase.auth.getSession();
      const userId = session?.data?.session?.user?.id;
      
      if (userId) {
        const { data: userProfile, error: profileError } = await supabase
          .from('profiles')
          .select('congregation_name')
          .eq('id', userId)
          .maybeSingle();
          
        if (profileError) {
          console.error("Error fetching user profile:", profileError);
        }
        
        let churchName = userProfile?.congregation_name || 'Grace Awakens';
        
        // If we have a church name from the user profile, try to fetch that church's branding
        if (churchName && churchName !== 'Grace Awakens') {
          const { data } = await supabase
            .from('church_branding')
            .select('*')
            .eq('church_name', churchName)
            .maybeSingle();
          
          if (data) {
            console.log("Found church branding for user's congregation:", data.church_name);
            brandingData = {
              churchName: data.church_name,
              logoUrl: data.logo_url,
              primaryColor: data.primary_color || '#0F4C81',
              secondaryColor: data.secondary_color || '#2A6BAA',
              welcomeMessage: data.custom_welcome_message,
              churchCode: data.church_code
            };
            
            setBranding(brandingData);
            setLoading(false);
            return;
          }
        }
      }
      
      // Default to Grace Awakens branding
      console.log("Using default Grace Awakens branding");
      brandingData = {
        churchName: 'Grace Awakens',
        logoUrl: '/lovable-uploads/5c561338-e504-4c6d-810f-f9a17db22224.png',
        primaryColor: '#0F4C81',
        secondaryColor: '#2A6BAA',
        welcomeMessage: 'Join our vibrant community of believers supporting each other through prayer, reflection, and daily devotionals.',
        churchCode: 'GRACE'
      };
      
      setBranding(brandingData);
      
    } catch (err) {
      console.error('Error fetching church branding:', err);
      setError(err instanceof Error ? err : new Error('Unknown error occurred'));
      
      // Set default branding in case of error
      setBranding({
        churchName: 'Grace Awakens',
        logoUrl: '/lovable-uploads/5c561338-e504-4c6d-810f-f9a17db22224.png',
        primaryColor: '#0F4C81',
        secondaryColor: '#2A6BAA',
        welcomeMessage: 'Join our vibrant community of believers supporting each other through prayer, reflection, and daily devotionals.',
        churchCode: 'GRACE'
      });
      
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch data on initial load
  useEffect(() => {
    fetchChurchBranding();
    
    // Listen for custom event to refresh branding
    const handleBrandingUpdated = () => {
      console.log("Church branding updated event received");
      fetchChurchBranding();
    };
    
    window.addEventListener('church-branding-updated', handleBrandingUpdated);
    
    return () => {
      window.removeEventListener('church-branding-updated', handleBrandingUpdated);
    };
  }, [fetchChurchBranding]);

  // Public function to refresh branding data
  const refreshBranding = async () => {
    console.log("Manually refreshing church branding...");
    await fetchChurchBranding();
  };

  return (
    <ChurchBrandingContext.Provider value={{ branding, loading, error, refreshBranding }}>
      {children}
    </ChurchBrandingContext.Provider>
  );
};
